<template>
  <div
    class="ds-tooltip__container"
    :class="{ 'ds-tooltip__container--inline': inline }"
    @mouseenter="showTooltip"
    @mouseleave="hideTooltip"
    @focusin="showTooltip"
    @focusout="hideTooltip"
    @keydown.esc="hideTooltip"
  >
    <div
      class="ds-tooltip"
      :class="tooltipClasses"
      v-if="label"
    >
      {{ label }}
    </div>
    <slot />
  </div>
</template>

<script>
export default {
  name: "Tooltip",
  props: {
    label: {
      type: String,
      required: true
    },
    tooltipClass: {
      type: String,
      default: ""
    },
    inline: {
      type: Boolean,
      default: false
    },
    large: {
      type: Boolean,
      default: false
    },
    position: {
      type: String,
      default: "top",
      validator: value => {
        return ["top-left", "top", "top-right", "bottom-left", "bottom", "bottom-right"].includes(value);
      }
    },
  },
  data() {
    return {
      visible: false
    };
  },
  computed: {
    tooltipClasses() {
      return [
        this.tooltipClass,
        this.positionClass,
        {
          'ds-tooltip--visible': this.visible,
          'ds-tooltip--large': this.large
        }
      ]
    },
    positionClass() {
      return `ds-tooltip--position-${this.position}`;
    }
  },
  mounted() {
    this.addTabIndexToSlot();
  },
  updated() {
    this.addTabIndexToSlot();
  },
  methods: {
    /**
     * Best accessibility practices require tooltip triggers (whatever you hover to make it show)
     * to be "focusable" and to show the tooltip on focus. Since this component uses a slot for
     * its trigger, we can't declaratively control whether or not it's focusable.
     * 
     * Instead, we add `tabindex="0"` to the first element in the slot if possible.
     * 
     * If there isn't a slot, or isn't a first element, or something else goes wrong, it's not
     * a big deal, and we'd rather swallow the error than break something else since the consequences
     * of having a non-focusable trigger are minimal.
     */
    addTabIndexToSlot() {
      try {
        const slotElement = this.$slots.default[0].elm;
        if (slotElement.tabIndex < 0) {
          slotElement.tabIndex = 0;
        }
      } catch (e) {} // eslint-disable-line no-empty
    },
    showTooltip() {
      this.visible = true;
    },
    hideTooltip() {
      this.visible = false;
    }
  }
};
</script>

<style lang="scss" scoped>
  @import "~StyleguideAssets/scss/master.scss";
</style>